import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  TextField
} from '@mui/material';
import React from 'react';
import { auth } from '../../../lib/google/firebase';
import {
  MultiFactorSession,
  TotpMultiFactorAssertion,
  TotpMultiFactorGenerator,
  TotpSecret,
  multiFactor
} from 'firebase/auth';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { generateUuidV4 } from '../../../util/uuid';
import PrimaryButton from '../../shared/button/PrimaryButton';
import GhostButton from '../../shared/button/GhostButton';
import { QRCode } from 'react-qr-code';
import { LoginState } from './LoginModal';
import { BodyText, Subheading } from '../../shared/typography/PrimaryTypography';
import { FirebaseError } from 'firebase/app';
import { log } from '../../../util/log';
import { MetadataError } from '@ep/error-handling';
import { MFA_TIMEOUT_MS } from '../../../env';

/**
 * Handles the enrollment of client authenticator Multifactor.
 * @param {(authenticated: boolean): void} setAuthenticated Sets the global authenticated state of the application
 * @param {(): void} closeLogin Closes the sign in process completely and resets it to initial sign in step.
 * @param {(login_state: LoginState): void} handleLoginState Handles the changing of modal state to various parts of the login flow.
 * @returns {JSX.Element}
 */
const EnrollAuthenticatorMfa = ({
  setAuthenticated,
  closeLogin,
  handleLoginState
}: {
  setAuthenticated: (authenticated: boolean) => void;
  closeLogin: () => void;
  handleLoginState: (login_state: LoginState) => void;
}): JSX.Element => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const [enrollConfirmed, setEnrollConfirmed] = React.useState<boolean>(false);
  const [codeVerifyLoad, setCodeVerifyLoad] = React.useState<boolean>(false);
  const [code, setCode] = React.useState<string>('');
  const [secret, setSecret] = React.useState<TotpSecret>();
  const [totpUri, setTotpUri] = React.useState<string>();
  const [validCode, setValidCode] = React.useState<boolean>(false);
  const [qrLoading, setQrLoading] = React.useState<boolean>(true);

  // Handles cancellation of Authenticator enrollment and return sign in state to initial state.
  const handleCancel = (): void => {
    auth.signOut();
    handleLoginState('login_identifier');
    closeLogin();
  };

  // Handles of closing the MFA verification before it times out to force user to restart process
  React.useEffect(() => {
    const timeout_id: NodeJS.Timeout = setTimeout(async () => {
      handleCancel();
    }, MFA_TIMEOUT_MS);
    return () => clearTimeout(timeout_id);
  }, [enrollConfirmed]);

  // Generates the Text and QR code to link account to Authenticator App.
  const handleTotpCodeGeneration = async (): Promise<void> => {
    try {
      if (auth.currentUser && auth.currentUser.email) {
        const multiFactorSession: MultiFactorSession = await multiFactor(
          auth.currentUser
        ).getSession();
        const totpSecret: TotpSecret = await TotpMultiFactorGenerator.generateSecret(
          multiFactorSession
        ).catch((err: unknown) => {
          throw new Error((err as FirebaseError).code);
        });
        setSecret(totpSecret);
        const totpSecretQR: string = totpSecret.generateQrCodeUrl(
          auth.currentUser.email,
          `Syntiro`
        );
        setTotpUri(totpSecretQR);
        setQrLoading(false);
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.message === 'auth/requires-recent-login') {
        setFeedbackData({
          message: 'Error: Signin session expired, please try again',
          state: true,
          type: 'error'
        });
        handleCancel();
      }
    }
  };

  // Handles the verification of the code to finalize link of account to Authenticator.
  const handleCodeVerification = async (
    event: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    event.preventDefault();
    try {
      if (secret && auth.currentUser) {
        setCodeVerifyLoad(true);
        const multifactor_assertion: TotpMultiFactorAssertion =
          TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
        await multiFactor(auth.currentUser)
          .enroll(multifactor_assertion, 'authenticator')
          .catch((err: unknown) => {
            throw new Error((err as FirebaseError).code);
          });
        setAuthenticated(true);
        setCodeVerifyLoad(false);
        closeLogin();
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: EnrollAuthenticatorMfa failed on an unknown error while calling handleCodeVerification.',
          { 'auth.currentUser': auth.currentUser },
          tracking_id
        )
      );
      if ((err as Error).message === 'auth/invalid-verification-code') {
        setFeedbackData({
          message: `Error: Invalid code, please try again. Tracking ID: ${tracking_id}`,
          state: true,
          type: 'error'
        });
      } else if ((err as Error).message === 'auth/too-many-enrollment-attempts') {
        setFeedbackData({
          message: `Error: Too many failed attempts, try again later. Tracking ID: ${tracking_id}`,
          state: true,
          type: 'error'
        });
        handleCancel();
      } else {
        setFeedbackData({
          message: 'Error: Unknown ',
          state: true,
          type: 'error'
        });
        handleCancel();
      }
    } finally {
      setCodeVerifyLoad(false);
    }
  };

  // Triggers generation of codes on first render.
  React.useEffect(() => {
    handleTotpCodeGeneration();
  }, []);

  React.useEffect(() => {
    if (code.length === 6) {
      setValidCode(true);
    }
  }, [code]);

  return (
    <Box>
      <Subheading weight="Medium" sx={{ mb: 2 }}>
        Authenticator Two-Factor Enrollment
      </Subheading>
      {!enrollConfirmed && (
        <Box>
          <BodyText sx={{ mb: 2 }}>
            Please scan the QR Code or type the below key into your chosen authenticator app then
            click next.
          </BodyText>
          <form>
            <Box textAlign={'center'} justifyContent={'center'}>
              {totpUri && <QRCode value={totpUri} />}
              {qrLoading && (
                <Box height={300} width={256} alignContent={'center'} justifySelf={'center'}>
                  {' '}
                  <CircularProgress size={200} />{' '}
                </Box>
              )}
              <BodyText sx={{ my: 2 }}>{secret?.secretKey}</BodyText>
            </Box>
            <DialogActions sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <PrimaryButton
                data-testid="verify-button"
                onClick={() => setEnrollConfirmed(true)}
                sx={{ minWidth: 180 }}
              >
                Next
              </PrimaryButton>
              <GhostButton
                onClick={() => handleLoginState('mfa_selection')}
                id="send-code-button"
                outlined
                dark
                sx={{ minWidth: 180 }}
              >
                Back
              </GhostButton>
            </DialogActions>
          </form>
        </Box>
      )}
      {enrollConfirmed && (
        <Box>
          <DialogContentText sx={{ mb: 2 }}>
            Please enter the newly generated code from your Authenticator App.
          </DialogContentText>
          <form>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                id="name"
                label="Verification Code"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setCode(event.target.value)
                }
              />
            </DialogContent>
            <DialogActions sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <PrimaryButton
                data-testid="verify-button"
                onClick={handleCodeVerification}
                loading={codeVerifyLoad}
                disabled={!validCode}
                sx={{ minWidth: 180 }}
                type="submit"
              >
                Verify
              </PrimaryButton>

              <GhostButton
                onClick={() => setEnrollConfirmed(false)}
                id="send-code-button"
                outlined
                dark
                type="submit"
                sx={{ minWidth: 180 }}
              >
                Back
              </GhostButton>
            </DialogActions>
          </form>
        </Box>
      )}
    </Box>
  );
};

export default EnrollAuthenticatorMfa;
